import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Instagram, Facebook, Search, Plus, X } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { usePublicSubscriptionStatus } from '@/hooks/usePublicSubscriptionStatus';
import { ScrollImageSection } from '@/components/ScrollImageSection';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import SEO from '@/components/SEO';
const cateringSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().max(20, "Phone number must be less than 20 characters").optional(),
  message: z.string().trim().max(1000, "Message must be less than 1000 characters").optional(),
  desiredDate: z.string().min(1, "Desired date is required"),
  numberOfServings: z.number().min(1, "Number of servings must be at least 1"),
  selectedItems: z.array(z.object({
    itemId: z.string(),
    itemName: z.string(),
    price: z.string(),
    quantity: z.number().min(1)
  })).min(1, "Please select at least one item")
});

const Home = () => {
  const { t } = useLanguage();
  const { socialMedia } = useSiteSettings();
  const { hasReservationsAccess, hasCateringAccess, hasContactsAccess, tier } = usePublicSubscriptionStatus();
  const [cateringForm, setCateringForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    desiredDate: '',
    numberOfServings: 1,
    selectedItems: [] as Array<{ itemId: string; itemName: string; price: string; quantity: number }>
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>({});
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .order('name');

      if (error) throw error;
      setMenuItems(data || []);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('home_settings')
        .select('*');

      if (error) throw error;

      if (data && data.length > 0) {
        const settingsObj: any = {};
        data.forEach((row) => {
          settingsObj[row.section] = row.data;
        });

        setSettings({
          hero: settingsObj.hero || {},
          story: settingsObj.story || {},
          drinks: settingsObj.drinks || {},
          social: settingsObj.social || {},
          private_dining: settingsObj.private_dining || {},
          catering: settingsObj.catering || {},
          cta: settingsObj.cta || {}
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load homepage content');
    } finally {
      setLoading(false);
    }
  };

  const handleCateringSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const validatedData = cateringSchema.parse(cateringForm);

      const { error } = await supabase
        .from('catering_requests')
        .insert([{
          name: validatedData.name,
          email: validatedData.email,
          phone_number: validatedData.phone || null,
          message: validatedData.message || null,
          desired_date: validatedData.desiredDate,
          number_of_servings: validatedData.numberOfServings,
          selected_items: validatedData.selectedItems,
          total_amount: calculateGrandTotal()
        }]);

      if (error) throw error;

      toast.success(t('cateringSent'));
      
      setCateringForm({
        name: '',
        email: '',
        phone: '',
        message: '',
        desiredDate: '',
        numberOfServings: 1,
        selectedItems: []
      });
      setItemQuantities({});
      setMenuSearchQuery('');
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        toast.error(t('cateringFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCateringChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.id === 'numberOfServings' ? parseInt(e.target.value) || 1 : e.target.value;
    setCateringForm({
      ...cateringForm,
      [e.target.id]: value,
    });
  };

  const addMenuItem = (item: any) => {
    const existingIndex = cateringForm.selectedItems.findIndex(i => i.itemId === item.id);
    
    if (existingIndex >= 0) {
      toast.error(t('itemAlreadyAdded'));
      return;
    }
    
    // Add item with default quantity of 1
    setCateringForm({
      ...cateringForm,
      selectedItems: [...cateringForm.selectedItems, {
        itemId: item.id,
        itemName: item.name,
        price: item.price,
        quantity: 1
      }]
    });
    setItemQuantities({ ...itemQuantities, [item.id]: 1 });
    toast.success(`${item.name} ${t('added')}`);
  };

  const parsePrice = (priceStr: string): number => {
    const numericPrice = priceStr.replace(/[^0-9.]/g, '');
    return parseFloat(numericPrice) || 0;
  };

  const calculateItemTotal = (price: string, quantity: number): number => {
    return parsePrice(price) * quantity;
  };

  const calculateGrandTotal = (): number => {
    return cateringForm.selectedItems.reduce((total, item) => {
      return total + calculateItemTotal(item.price, item.quantity);
    }, 0);
  };

  const removeMenuItem = (itemId: string) => {
    const newItems = cateringForm.selectedItems.filter(i => i.itemId !== itemId);
    setCateringForm({ ...cateringForm, selectedItems: newItems });
    
    const newQuantities = { ...itemQuantities };
    delete newQuantities[itemId];
    setItemQuantities(newQuantities);
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) return;
    
    setItemQuantities({ ...itemQuantities, [itemId]: quantity });
    
    const newItems = cateringForm.selectedItems.map(item =>
      item.itemId === itemId ? { ...item, quantity } : item
    );
    setCateringForm({ ...cateringForm, selectedItems: newItems });
  };

  const filteredMenuItems = menuItems.filter(item =>
    item.name.toLowerCase().includes(menuSearchQuery.toLowerCase()) &&
    !cateringForm.selectedItems.some(selected => selected.itemId === item.id)
  );

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </main>
    );
  }

  if (!settings) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <p className="text-muted-foreground mb-4">{t('noContent')}</p>
          <p className="text-sm text-muted-foreground">{t('configureSettings')}</p>
        </div>
      </main>
    );
  }

  return (
    <main>
      <SEO />
      {/* Hero Section */}
      {settings.hero?.image_url && settings.hero?.title && (
        <ScrollImageSection
          imageUrl={settings.hero.image_url}
          title={settings.hero.title}
          subtitle={settings.hero.subtitle}
          minHeight="600px"
          imageAlt="Restaurant hero"
        >
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
            <Button size="lg" className="text-sm md:text-lg min-w-[180px] md:min-w-[200px]">
              {t('orderOnline')}
            </Button>
            {hasReservationsAccess && (
              <Button size="lg" variant="secondary" className="text-sm md:text-lg min-w-[180px] md:min-w-[200px]" asChild>
                <Link to="/reservations">
                  {t('reservations')}
                </Link>
              </Button>
            )}
            <Button size="lg" variant="secondary" className="text-sm md:text-lg min-w-[180px] md:min-w-[200px]" asChild>
              <Link to="/menu">
                {t('viewMenu')}
              </Link>
            </Button>
          </div>
        </ScrollImageSection>
      )}

      {/* Story Section */}
      {settings.story?.title && (
        <section className="py-12 md:py-20 px-4 border-b border-gray-200">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl lg:text-4xl font-bold font-['Playfair_Display'] text-black mb-4 md:mb-8">
                {settings.story.title}
              </h2>
              {settings.story.text && (
                <p className="text-sm md:text-lg lg:text-lg text-muted-foreground leading-relaxed">
                  {settings.story.text}
                </p>
              )}
            </div>

            {/* Photo Grid */}
            {(settings.story.image1_url || settings.story.image2_url || settings.story.image3_url) && (
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {settings.story.image1_url && (
                  <div className="overflow-hidden rounded-lg shadow-lg">
                    <img
                      src={settings.story.image1_url}
                      alt={`${t('storyImageAlt')} 1`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                {settings.story.image2_url && (
                  <div className="overflow-hidden rounded-lg shadow-lg">
                    <img
                      src={settings.story.image2_url}
                      alt={`${t('storyImageAlt')} 2`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                {settings.story.image3_url && (
                  <div className="overflow-hidden rounded-lg shadow-lg">
                    <img
                      src={settings.story.image3_url}
                      alt={`${t('storyImageAlt')} 3`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
              </div>
            )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Button size="lg" className="text-sm md:text-base" asChild>
              <Link to="/menu">{t('viewMenu')}</Link>
            </Button>
            <Button size="lg" variant="secondary" className="text-sm md:text-base">
              {t('orderNow')}
            </Button>
            {hasReservationsAccess && (
              <Button size="lg" variant="outline" className="text-sm md:text-base" asChild>
                <Link to="/reservations">
                  {t('reserveTable')}
                </Link>
              </Button>
            )}
          </div>
          </div>
        </section>
      )}

      {/* Drinks Section */}
      {(settings.drinks?.title || settings.drinks?.description) && settings.drinks.image_url && (
        <ScrollImageSection
          imageUrl={settings.drinks.image_url}
          minHeight="80vh"
          imageAlt="Drinks"
          overlayOpacity={0.3}
        >
          <div className="border-4 md:border-8 border-primary p-6 md:p-12 lg:p-20 text-center bg-white/95 max-w-5xl mx-auto">
            {settings.drinks.title && (
              <h2 className="text-xl md:text-3xl lg:text-5xl font-black text-black mb-4 md:mb-8 leading-tight font-sans">
                {settings.drinks.title}
              </h2>
            )}
            {settings.drinks.description && (
              <p className="text-sm md:text-lg lg:text-xl font-bold text-black mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed font-sans">
                {settings.drinks.description}
              </p>
            )}
            <Button size="lg" className="bg-black text-white hover:bg-black/90 text-sm md:text-lg px-8 md:px-12 rounded-lg" asChild>
              <Link to="/menu">{t('ourDrinksButton')}</Link>
            </Button>
          </div>
        </ScrollImageSection>
      )}
      
      {(settings.drinks?.title || settings.drinks?.description) && !settings.drinks.image_url && (
        <section className="py-12 md:py-20 px-4 border-b border-gray-200">
          <div className="container mx-auto max-w-5xl">
            <div className="border-4 md:border-8 border-primary p-6 md:p-12 lg:p-20 text-center bg-white">
              {settings.drinks.title && (
                <h2 className="text-xl md:text-3xl lg:text-5xl font-black text-black mb-4 md:mb-8 leading-tight font-sans">
                  {settings.drinks.title}
                </h2>
              )}
              {settings.drinks.description && (
                <p className="text-sm md:text-lg lg:text-xl font-bold text-black mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed font-sans">
                  {settings.drinks.description}
                </p>
              )}
              <Button size="lg" className="bg-black text-white hover:bg-black/90 text-sm md:text-lg px-8 md:px-12 rounded-lg" asChild>
                <Link to="/menu">{t('ourDrinksButton')}</Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Follow Our Social Media */}
      {settings.social?.title && (
        <section className="py-12 md:py-20 px-4 border-b border-gray-200">
          <div className="container mx-auto">
            <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl lg:text-4xl font-bold font-['Playfair_Display'] mb-4 md:mb-6 text-black">
                {settings.social.title}
              </h2>
            <div className="flex justify-center gap-4 mb-6">
              {socialMedia.instagram && (
                <a
                  href={socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-6 h-6" />
                </a>
              )}
              {socialMedia.tiktok && (
                <a
                  href={socialMedia.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
                  aria-label="TikTok"
                >
                  <FaTiktok className="w-6 h-6" />
                </a>
              )}
              {socialMedia.facebook && (
                <a
                  href={socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-6 h-6" />
                </a>
              )}
            </div>
            {settings.social.description && (
              <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto">
                {settings.social.description}
              </p>
            )}
          </div>

          {(settings.social.image1_url || settings.social.image2_url || settings.social.image3_url || settings.social.image4_url) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
               {settings.social.image1_url && (
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img
                    src={settings.social.image1_url}
                    alt={`${t('socialImageAlt')} 1`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              )}
              {settings.social.image2_url && (
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img
                    src={settings.social.image2_url}
                    alt={`${t('socialImageAlt')} 2`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              )}
              {settings.social.image3_url && (
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img
                    src={settings.social.image3_url}
                    alt={`${t('socialImageAlt')} 3`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              )}
              {settings.social.image4_url && (
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img
                    src={settings.social.image4_url}
                    alt={`${t('socialImageAlt')} 4`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              )}
            </div>
          )}
          </div>
        </section>
      )}

      {/* Private Dining & Events */}
      {settings.private_dining?.title && settings.private_dining?.image_url && (
        <ScrollImageSection
          imageUrl={settings.private_dining.image_url}
          title={settings.private_dining.title}
          subtitle={settings.private_dining.description}
          minHeight="80vh"
          imageAlt="Private dining area"
        >
          {hasReservationsAccess && (
            <Button size="lg" className="text-sm md:text-lg" asChild>
              <Link to="/reservations">
                {t('reserveNow')}
              </Link>
            </Button>
          )}
        </ScrollImageSection>
      )}

      {/* Catering Section */}
      {hasCateringAccess && settings.catering?.title && (
        <section className="py-12 md:py-20 px-4 border-b border-gray-200">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl lg:text-4xl font-bold font-['Playfair_Display'] text-black mb-4 md:mb-6">
                {settings.catering.title}
              </h2>
              {settings.catering.description && (
                <p className="text-sm md:text-lg lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                  {settings.catering.description}
                </p>
              )}
            </div>

          <Card className="p-4 md:p-8 max-w-2xl mx-auto">
            <h3 className="text-lg md:text-2xl font-bold mb-4 md:mb-6 text-center">{t('requestCateringInfo')}</h3>
            <form onSubmit={handleCateringSubmit} className="space-y-4 md:space-y-6">
              <div>
                <Label htmlFor="name" className="text-xs md:text-sm">{t('name')} *</Label>
                <Input
                  id="name"
                  value={cateringForm.name}
                  onChange={handleCateringChange}
                  required
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-xs md:text-sm">{t('email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={cateringForm.email}
                  onChange={handleCateringChange}
                  required
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs md:text-sm">{t('phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={cateringForm.phone}
                  onChange={handleCateringChange}
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="desiredDate" className="text-xs md:text-sm">Desired Date *</Label>
                <Input
                  id="desiredDate"
                  type="date"
                  value={cateringForm.desiredDate}
                  onChange={handleCateringChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="numberOfServings" className="text-xs md:text-sm">Number of Servings *</Label>
                <Input
                  id="numberOfServings"
                  type="number"
                  min="1"
                  value={cateringForm.numberOfServings}
                  onChange={handleCateringChange}
                  required
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label className="text-xs md:text-sm">Select Menu Items *</Label>
                
                {/* Selected Items */}
                {cateringForm.selectedItems.length > 0 && (
                  <div className="mt-2 mb-3 border rounded-md p-2 md:p-4 bg-muted/30">
                    <p className="text-[10px] md:text-xs font-semibold mb-2 md:mb-3">Selected Items ({cateringForm.selectedItems.length})</p>
                    <div className="space-y-1.5 md:space-y-2">
                      {cateringForm.selectedItems.map((item, index) => (
                        <div key={item.itemId} className="flex items-start md:items-center gap-1 md:gap-2 bg-background p-1.5 md:p-3 rounded text-[10px] md:text-sm">
                          <span className="text-[9px] md:text-xs text-muted-foreground min-w-[12px] md:min-w-[16px]">{index + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate leading-tight">{item.itemName}</div>
                            <div className="flex items-center gap-1 mt-0.5 md:hidden">
                              <span className="text-[9px] text-muted-foreground">{item.price}</span>
                              <span className="text-[9px] text-muted-foreground">×</span>
                              <Input
                                type="number"
                                min="1"
                                value={itemQuantities[item.itemId] || 1}
                                onChange={(e) => updateItemQuantity(item.itemId, parseInt(e.target.value) || 1)}
                                className="w-10 h-5 text-[10px] p-0.5 text-center"
                                placeholder="Qty"
                              />
                            </div>
                          </div>
                          <div className="hidden md:flex items-center gap-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{item.price}</span>
                            <span className="text-xs text-muted-foreground">×</span>
                            <Input
                              type="number"
                              min="1"
                              value={itemQuantities[item.itemId] || 1}
                              onChange={(e) => updateItemQuantity(item.itemId, parseInt(e.target.value) || 1)}
                              className="w-16 h-8 text-xs"
                              placeholder="Qty"
                            />
                          </div>
                          <span className="text-[10px] md:text-xs font-semibold min-w-[40px] md:min-w-[60px] text-right">
                            ${calculateItemTotal(item.price, item.quantity).toFixed(2)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMenuItem(item.itemId)}
                            className="h-5 w-5 md:h-8 md:w-8 p-0"
                          >
                            <X className="h-3 w-3 md:h-4 md:w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 md:mt-4 pt-2 md:pt-3 border-t flex justify-between items-center">
                      <span className="text-xs md:text-sm font-bold">Total Quote:</span>
                      <span className="text-base md:text-lg font-bold text-primary">
                        ${calculateGrandTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Search Bar */}
                <div className="mt-2 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search menu items..."
                    value={menuSearchQuery}
                    onChange={(e) => setMenuSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                    className="pl-9 text-sm md:text-base h-9 md:h-10"
                  />
                </div>

                {/* Available Items */}
                {isSearchFocused && (
                  <div className="mt-2 border rounded-md max-h-48 overflow-y-auto">
                    {filteredMenuItems.length > 0 ? (
                      <div className="divide-y">
                        {filteredMenuItems.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="text-xs text-muted-foreground">{item.price}</p>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => addMenuItem(item)}
                              className="h-8"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-muted-foreground py-4">No items found</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="message" className="text-xs md:text-sm">Additional Notes</Label>
                <Textarea
                  id="message"
                  value={cateringForm.message}
                  onChange={handleCateringChange}
                  rows={3}
                  className="mt-2 text-sm md:text-base"
                  placeholder="Special requirements, dietary restrictions, etc..."
                />
              </div>

              <Button type="submit" className="w-full text-sm md:text-base" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Request Catering Information'}
              </Button>
            </form>
          </Card>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {settings.cta?.title && settings.cta?.image_url && (
        <section className="relative py-20 md:py-32 px-4 overflow-hidden mb-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${settings.cta.image_url})` }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="relative z-10 container mx-auto text-center text-white">
            <h2 className="text-2xl md:text-4xl lg:text-4xl font-bold font-['Playfair_Display'] mb-4 md:mb-6">
              {settings.cta.title}
            </h2>
            {settings.cta.subtitle && (
              <p className="text-base md:text-xl mb-6 md:mb-8 text-white/90 max-w-2xl mx-auto">
                {settings.cta.subtitle}
              </p>
            )}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Button size="lg" variant="secondary" className="text-sm md:text-lg" asChild>
              <Link to="/locations">{t('getDirections')}</Link>
            </Button>
            <Button size="lg" className="text-sm md:text-lg bg-white text-primary hover:bg-white/90">
              {t('orderNow')}
            </Button>
          </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Home;
